import { baseApi } from './baseApi';
import { ServiceTypes } from '../constants';
import {
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerMimeTypes,
} from '../interfaces';
import { addNotification } from '../store';
import { isAbortError } from '../helpers/apiErrors';
import { captureError } from '@clatter/platform';

const mapEtlFunctionsToSelectorOptions = (item: EtlFunction) => ({
  label: item.DisplayName,
  value: item.Arn,
});

const mapProjectTemplateToStore = (item: ProjectTemplate) => ({
  id: item.ID,
  displayName: item.Name,
  slug: item.Slug,
  type: FolderManagerMimeTypes.FOLDER,
  itemData: item,
  documentTemplates: item?.DocumentTemplates,
  status: item?.Status,
  features: item?.Features,
  updatedAt: item.UpdatedAt,
  createdAt: item.CreatedAt,
  deletedAt: item.DeletedAt,
  actions: [
    { name: FolderManagerItemActionNames.EDIT, type: FolderManagerItemActionTypes.SINGLE },
    { name: FolderManagerItemActionNames.EXPORT, type: FolderManagerItemActionTypes.SINGLE },
    { name: FolderManagerItemActionNames.CLONE, type: FolderManagerItemActionTypes.SINGLE },
    { name: FolderManagerItemActionNames.RESTORE, type: FolderManagerItemActionTypes.MULTI },
    { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
  ],
});

export const projectTemplatesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    //region PROJECT TEMPLATES
    getProjectTemplates: builder.query<any[], void>({
      query: () => `/project-templates`,
      transformResponse: (result: { data: ProjectTemplate[] }) => result.data.map(mapProjectTemplateToStore),
      providesTags: (result) => (result ? [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' }] : []),
    }),
    getProjectTemplateById: builder.query<ProjectTemplate, { workspaceId: number }>({
      query: ({ workspaceId }) => `/project-templates/${workspaceId}`,
      transformResponse: (result: { data: ProjectTemplate }) => result.data,
      providesTags: (result, error, { workspaceId }) =>
        result
          ? [
              {
                type: ServiceTypes.PROJECT_TEMPLATES,
                id: workspaceId,
              },
              { type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' },
            ]
          : [],
    }),
    getProjectTemplatesCards: builder.query<any[], void>({
      query: () => `/project-templates`,
      transformResponse: (result: { data: any[] }) =>
        result.data.filter((item) => item.Status?.toLowerCase() !== 'not published').map(mapProjectTemplateToStore),
      providesTags: (result) => (result ? [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'CARDS' }] : []),
    }),
    getEtlFunctionsAsSelectOptions: builder.query<IDropdownSelectOptionItem[], void>({
      query: () => `/etl-functions`,
      transformResponse: (result: { data: EtlFunction[] }) => result.data.map(mapEtlFunctionsToSelectorOptions),
      providesTags: (result) => (result ? [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'ETL_FUNCTIONS' }] : []),
    }),
    updateProjectTemplate: builder.mutation<
      void,
      {
        projectTemplateId: number;
        data: Partial<ProjectTemplateUpdatePayload>;
      }
    >({
      query: ({ projectTemplateId, data }) => ({
        url: `/project-templates/${projectTemplateId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { projectTemplateId }) =>
        result
          ? [
              { type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' },
              { type: ServiceTypes.PROJECT_TEMPLATES, id: 'CARDS' },
              { type: ServiceTypes.PROJECT_TEMPLATES, id: projectTemplateId },
            ]
          : [],
    }),
    createProjectTemplate: builder.mutation<ProjectTemplate, { data: ProjectTemplateCreatePayload }>({
      query: ({ data }) => ({
        url: `/project-templates`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (result: { data: ProjectTemplate }) => result.data,
      invalidatesTags: (result) => (result ? [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' }] : []),
    }),
    exportProjectTemplate: builder.mutation<ProjectTemplate, { projectTemplateId: number; type?: 'zip' | 'json' }>({
      query: ({ projectTemplateId, type = 'zip' }) => ({
        url: `/project-templates/${projectTemplateId}/exports?type=${type}`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
        transformResponse: (result: any) => result.data,
      }),
    }),
    cloneProjectTemplate: builder.mutation<ProjectTemplate, { projectTemplateId: number }>({
      query: ({ projectTemplateId }) => ({
        url: `/project-templates/${projectTemplateId}/clone`,
        method: 'POST',
        transformResponse: (result: any) => result.data,
      }),
    }),
    importProjectTemplate: builder.mutation<ProjectTemplate, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `/project-templates/imports?type=zip`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (result: { data: ProjectTemplate }) => result.data,
      invalidatesTags: (result) => [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' }],
    }),
    //endregion

    //region QUERIES
    getProjectTemplateQueries: builder.query<WorkspaceQuery[], { projectTemplateId: number }>({
      query: ({ projectTemplateId }) => `/project-templates/${projectTemplateId}/queries`,
      transformResponse: (result: { data: any[] }) => result.data,
      providesTags: (result) =>
        result
          ? [
              {
                type: ServiceTypes.PROJECT_TEMPLATES,
                id: 'PROJECT_TEMPLATE_QUERIES',
              },
            ]
          : [],
    }),
    createProjectTemplateQuery: builder.mutation<WorkspaceQuery, { projectTemplateId: number; data: any }>({
      query: ({ data, projectTemplateId }) => ({
        url: `/project-templates/${projectTemplateId}/queries`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (result: { data: WorkspaceQuery }) => result.data,
      invalidatesTags: () => [
        {
          type: ServiceTypes.PROJECT_TEMPLATES,
          id: 'PROJECT_TEMPLATE_QUERIES',
        },
      ],
    }),
    updateProjectTemplateQuery: builder.mutation<
      WorkspaceQuery,
      {
        projectTemplateId: number;
        queryId: number;
        data: any;
      }
    >({
      query: ({ data, projectTemplateId, queryId }) => ({
        url: `/project-templates/${projectTemplateId}/queries/${queryId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (result: { data: WorkspaceQuery }) => result.data,
      invalidatesTags: () => [
        {
          type: ServiceTypes.PROJECT_TEMPLATES,
          id: 'PROJECT_TEMPLATE_QUERIES',
        },
      ],
    }),
    deleteProjectTemplateQuery: builder.mutation<any, { projectTemplateId: number; queryId: number }>({
      query: ({ projectTemplateId, queryId }) => ({
        url: `/project-templates/${projectTemplateId}/queries/${queryId}`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ projectTemplateId, queryId }, { dispatch, queryFulfilled }) => {
        // using optimistic update with redo on error

        let deletedItem: WorkspaceQuery | undefined;

        const patchResult = dispatch(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          baseApi.util.updateQueryData('getProjectTemplateQueries', { projectTemplateId }, (draft) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            const index = draft.findIndex((query) => query.ID === queryId);
            if (index !== -1) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              deletedItem = { ...draft[index] };
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              draft.splice(index, 1);
            }
          }),
        );

        try {
          await queryFulfilled;

          dispatch(
            addNotification({
              type: 'success',
              message: `Workspace query "${deletedItem?.DisplayName}" has been successfully deleted.`,
            }),
          );
        } catch (error) {
          if (isAbortError(error)) return;
          const message = `Couldn't delete the query "${deletedItem?.DisplayName}". Please try again.`;
          patchResult.undo();
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          captureError(error, { message, component: 'projectTemplates.service' });
        }
      },
    }),
    //endregion

    //region DOCUMENT TEMPLATES
    getDocumentTemplateById: builder.query<
      DocumentTemplate,
      {
        documentTemplateId: number;
        projectTemplateId: number;
      }
    >({
      query: ({ documentTemplateId, projectTemplateId }) => ({
        url: `/project-templates/${projectTemplateId}/document-templates/${documentTemplateId}`,
        method: 'GET',
      }),
      transformResponse: (result: INgApiResponse<DocumentTemplate>) => result.data,
      providesTags: (result, error, { documentTemplateId }) => [
        {
          type: ServiceTypes.PROJECT_TEMPLATES,
          id: `DOCUMENT_TEMPLATES_${documentTemplateId}`,
        },
        { type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' },
      ],
    }),
    createDocumentTemplate: builder.mutation<any, { projectTemplateId: number; data: any }>({
      query: ({ data, projectTemplateId }) => ({
        url: `/project-templates/${projectTemplateId}/document-templates`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' }],
    }),
    updateDocumentTemplate: builder.mutation<
      any,
      {
        projectTemplateId: number;
        documentTemplateId: number;
        data: any;
      }
    >({
      query: ({ data, projectTemplateId, documentTemplateId }) => ({
        url: `/project-templates/${projectTemplateId}/document-templates/${documentTemplateId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { projectTemplateId, documentTemplateId }) => [
        { type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' },
        { type: ServiceTypes.PROJECT_TEMPLATES, id: 'CARDS' },
        { type: ServiceTypes.PROJECT_TEMPLATES, id: projectTemplateId },
        { type: ServiceTypes.PROJECT_TEMPLATES, id: `DOCUMENT_TEMPLATES_${documentTemplateId}` },
      ],
    }),
    deleteDocumentTemplate: builder.mutation<
      any,
      {
        projectTemplateId: number;
        documentTemplateId: number;
      }
    >({
      query: ({ projectTemplateId, documentTemplateId }) => ({
        url: `/project-templates/${projectTemplateId}/document-templates/${documentTemplateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' }],
    }),
    moveToTrash: builder.mutation<any, { projectTemplateId: number }>({
      query: ({ projectTemplateId }) => ({
        url: `/project-templates/${projectTemplateId}`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ projectTemplateId }, { dispatch, queryFulfilled }) => {
        let deletedItem: ProjectTemplateFromStore | undefined;

        const patchResult = dispatch(
          //@ts-ignore
          baseApi.util.updateQueryData('getProjectTemplates', undefined, (draft: any) => {
            const index = draft.findIndex((item: ProjectTemplateFromStore) => {
              const itemFromStore = JSON.parse(JSON.stringify(item));
              return itemFromStore.id === projectTemplateId;
            });
            if (index !== -1) {
              deletedItem = { ...draft[index] };
              draft.splice(index, 1);
            }
          }),
        );

        try {
          await queryFulfilled;

          dispatch(
            addNotification({
              type: 'success',
              message: `Workspace template "${deletedItem?.displayName}" has been successfully moved to trash.`,
            }),
          );
        } catch (error) {
          if (isAbortError(error)) return;
          const message = `Error moving to trash workspace template: "${deletedItem?.displayName}"!`;
          patchResult.undo();
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          captureError(error, { message, component: 'projectTemplates.service' });
        }
      },
      invalidatesTags: () => [{ type: ServiceTypes.PROJECT_TEMPLATES, id: 'LIST' }],
    }),
    //endregion
  }),
});

export const {
  // project template
  useGetProjectTemplatesQuery,
  useGetProjectTemplateByIdQuery,
  useGetProjectTemplatesCardsQuery,
  useCreateProjectTemplateMutation,
  useImportProjectTemplateMutation,
  useExportProjectTemplateMutation,
  useCloneProjectTemplateMutation,
  useUpdateProjectTemplateMutation,
  useMoveToTrashMutation,
  useGetEtlFunctionsAsSelectOptionsQuery,

  // document templates
  useGetDocumentTemplateByIdQuery,
  useCreateDocumentTemplateMutation,
  useUpdateDocumentTemplateMutation,
  useDeleteDocumentTemplateMutation,

  // queries
  useGetProjectTemplateQueriesQuery,
  useCreateProjectTemplateQueryMutation,
  useUpdateProjectTemplateQueryMutation,
  useDeleteProjectTemplateQueryMutation,
} = projectTemplatesService;
